import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  subirEntrega,
  obtenerAsignacionesPorClase,
  obtenerEntregasPorAlumno,
} from "./constants";
import { useData } from "./DataContext";
import "./PaginaInicio.css";
import "./HomeworkModule.css";
import Cabecera from "../Others/Cabecera";
import PiePagina from "../Others/PiePagina";
import BarraSuperior from "../Others/BarraSuperior";
import BarraInferior from "../Others/BarraInferior";
import NavigationIcon from "../Others/NavigationIcon";

const formatDate = (value) => {
  if (!value) return "Sin fecha límite";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
};

const HomeworkPage = () => {
  const { dataClase, dataAlumno } = useData();
  const [tareas, setTareas] = useState([]);
  const [calificaciones, setCalificaciones] = useState({});
  const [comentarios, setComentarios] = useState({});
  const [selectedFile, setSelectedFile] = useState(null);
  const [status, setStatus] = useState({ type: "", text: "" });
  const [loadingTareas, setLoadingTareas] = useState(true);
  const [loadingCalificaciones, setLoadingCalificaciones] = useState(true);
  const [uploadingId, setUploadingId] = useState(null);

  const loading = loadingTareas || loadingCalificaciones;

  const fetchTasks = async () => {
    try {
      setLoadingTareas(true);
      const url = obtenerAsignacionesPorClase(dataClase.matricula_clase);
      const response = await axios.get(url);
      setTareas(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error al obtener las tareas:", error);
      setTareas([]);
      setStatus({
        type: "error",
        text: "No fue posible cargar las tareas de esta clase.",
      });
    } finally {
      setLoadingTareas(false);
    }
  };

  const fetchCalificaciones = async () => {
    try {
      setLoadingCalificaciones(true);
      const response = await axios.get(
        obtenerEntregasPorAlumno(dataAlumno.id_alumno)
      );

      if (response.data.success && Array.isArray(response.data.data)) {
        const nuevasCalificaciones = {};
        const nuevosComentarios = {};

        response.data.data.forEach((entrega) => {
          nuevasCalificaciones[entrega.id_asignacion] = entrega.calificacion;
          nuevosComentarios[entrega.id_asignacion] =
            entrega.comentarios || "Sin comentarios";
        });

        setCalificaciones(nuevasCalificaciones);
        setComentarios(nuevosComentarios);
      } else {
        setCalificaciones({});
        setComentarios({});
      }
    } catch (error) {
      console.error("Error al obtener calificaciones:", error);
      setStatus({
        type: "error",
        text: "Las tareas se cargaron, pero no fue posible consultar tus calificaciones.",
      });
    } finally {
      setLoadingCalificaciones(false);
    }
  };

  useEffect(() => {
    if (dataClase?.matricula_clase) {
      fetchTasks();
    }
  }, [dataClase?.matricula_clase]);

  useEffect(() => {
    if (dataAlumno?.id_alumno) {
      fetchCalificaciones();
    }
  }, [dataAlumno?.id_alumno]);

  const handleFileChange = (event) => {
    const file = event.target.files[0] || null;
    setSelectedFile(file);
    setStatus({ type: "", text: "" });
  };

  const handleSubmitPDF = async (event, tareaId) => {
    event.preventDefault();

    if (!tareaId || !selectedFile) {
      setStatus({
        type: "error",
        text: "Selecciona un archivo PDF antes de realizar la entrega.",
      });
      return;
    }

    try {
      setUploadingId(tareaId);
      setStatus({ type: "", text: "" });

      const formData = new FormData();
      formData.append("archivo", selectedFile);
      formData.append("id_asignacion", tareaId);
      formData.append("id_alumno", dataAlumno.id_alumno);

      const response = await axios.post(subirEntrega, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.status === 200 && response.data.success) {
        setStatus({
          type: "success",
          text: "Tu archivo se entregó correctamente.",
        });
        setSelectedFile(null);
        await fetchCalificaciones();
      } else {
        setStatus({
          type: "error",
          text:
            response.data.error ||
            "El servidor no pudo completar la entrega del archivo.",
        });
      }
    } catch (error) {
      if (error.response) {
        setStatus({
          type: "error",
          text:
            error.response.data.error ||
            "El servidor no pudo completar la entrega. Intenta nuevamente.",
        });
      } else if (error.request) {
        setStatus({
          type: "error",
          text: "No hubo respuesta del servidor. Verifica tu conexión.",
        });
      } else {
        setStatus({
          type: "error",
          text: "Ocurrió un error inesperado al entregar la tarea.",
        });
      }
    } finally {
      setUploadingId(null);
    }
  };

  const barraSuperior = (
    <BarraSuperior texto_cabecera={dataClase?.nombre_clase || "Ejercicios"} />
  );

  return (
    <div className="contenedor-pagina">
      <Cabecera
        contenidosuperior={barraSuperior}
        contenidoInferior={<BarraInferior />}
      />

      <main className="homework-module">
        <section className="homework-panel" aria-labelledby="student-homework-title">
          <header className="homework-hero">
            <div>
              <span className="homework-eyebrow">Actividades del curso</span>
              <h1 id="student-homework-title">Ejercicios</h1>
              <p>
                Consulta las indicaciones, entrega tus archivos y revisa la
                retroalimentación de cada actividad.
              </p>
            </div>
            <div className="homework-summary">
              <strong>{tareas.length}</strong>
              <span>{tareas.length === 1 ? "tarea disponible" : "tareas disponibles"}</span>
            </div>
          </header>

          <div className="homework-body">
            {status.text && (
              <div
                className={"homework-status " + (status.type ? "is-" + status.type : "")}
                role={status.type === "error" ? "alert" : "status"}
              >
                {status.text}
              </div>
            )}

            {loading ? (
              <div className="homework-loading" aria-live="polite">
                <span className="homework-spinner" />
                <strong>Cargando ejercicios</strong>
                <p>Estamos consultando las tareas y calificaciones de esta clase.</p>
              </div>
            ) : tareas.length === 0 ? (
              <div className="homework-empty">
                <span className="homework-empty-icon">
                  <NavigationIcon name="tasks" size={28} />
                </span>
                <strong>Aún no hay ejercicios disponibles</strong>
                <p>Las tareas que asigne tu profesor aparecerán en esta sección.</p>
              </div>
            ) : (
              <div className="homework-list">
                {tareas.map((tarea) => {
                  const tieneEntrega = Object.prototype.hasOwnProperty.call(
                    calificaciones,
                    tarea.id_asignacion
                  );
                  const estaCalificada =
                    tieneEntrega &&
                    calificaciones[tarea.id_asignacion] !== null &&
                    calificaciones[tarea.id_asignacion] !== "";
                  const estaSubiendo = uploadingId === tarea.id_asignacion;

                  return (
                    <article
                      key={tarea.id_asignacion}
                      className={
                        "homework-card " +
                        (estaCalificada
                          ? "is-graded"
                          : tieneEntrega
                            ? ""
                            : "is-pending")
                      }
                    >
                      <span className="homework-icon">
                        <NavigationIcon name="tasks" size={25} />
                      </span>

                      <div className="homework-card-main">
                        <span className="homework-kicker">Tarea del curso</span>
                        <h2>{tarea.nombre || "Tarea sin nombre"}</h2>
                        {tarea.instrucciones && <p>{tarea.instrucciones}</p>}
                        <div className="homework-card-meta">
                          <span>Fecha límite: {formatDate(tarea.fecha_limite)}</span>
                          {estaCalificada ? (
                            <span className="homework-badge is-success">
                              Calificación: {calificaciones[tarea.id_asignacion]}
                            </span>
                          ) : tieneEntrega ? (
                            <span className="homework-badge">
                              Entregada, pendiente de calificación
                            </span>
                          ) : (
                            <span className="homework-badge is-warning">
                              Pendiente de entrega
                            </span>
                          )}
                        </div>
                        {estaCalificada && (
                          <p>
                            Comentario: {comentarios[tarea.id_asignacion]}
                          </p>
                        )}
                      </div>

                      {!tieneEntrega && (
                        <form
                          className="homework-card-actions homework-upload"
                          onSubmit={(event) =>
                            handleSubmitPDF(event, tarea.id_asignacion)
                          }
                        >
                          <label className="homework-file-label" htmlFor={"homework-file-" + tarea.id_asignacion}>
                            Archivo PDF
                          </label>
                          <input
                            id={"homework-file-" + tarea.id_asignacion}
                            className="homework-file-input"
                            type="file"
                            accept=".pdf,application/pdf"
                            onChange={handleFileChange}
                          />
                          <button
                            className="homework-button"
                            type="submit"
                            disabled={estaSubiendo}
                          >
                            <NavigationIcon name="deliverables" size={18} />
                            {estaSubiendo ? "Entregando..." : "Entregar tarea"}
                          </button>
                        </form>
                      )}
                    </article>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      </main>

      <PiePagina imagenSrc={require("../Assets/piepagina.jpg")} />
      <footer>Grupo Derecho & Progreso &copy; 2024</footer>
    </div>
  );
};

export default HomeworkPage;
